import theme from '../../../../theme/theme';

/* ------------------------------------------------------------------ *
 * UserForm (maquette v4) design tokens and style objects.
 *
 * Pixel-match to the "Droits utilisateurs v4" maquette, kept beside UserForm (its
 * only consumer). These values are
 * deliberately NOT all mapped to the app theme palette: the maquette uses its own
 * blue (`#3D6F9C` ≠ `theme.palette.primary.main`) and its own greens/reds/borders.
 * The two surfaces that DO match the theme (page / section background) are read
 * from the theme tokens so there is a single source of truth for them.
 * ------------------------------------------------------------------ */
export const mq = {
  bg: theme.palette.background.page,
  cardBorder: '#D8DEE5',
  borderStrong: '#C3CBD6',
  headBg: theme.palette.background.section,
  text: '#0F172A',
  muted: '#475569',
  subtle: '#94A3B8',
  primary: '#3D6F9C',
  primaryHover: '#36648E',
  accentLight: '#E6F7FB',
  red: '#DC2626',
  redBorder: '#F0C6C6',
  redBg: '#FCEBEB',
  green: '#16A34A',
  greenDark: '#15803D',
} as const;

const btnBase = {
  height: 38,
  px: '16px',
  borderRadius: '6px',
  fontSize: '14px',
  fontWeight: 600,
  lineHeight: 1,
  textTransform: 'none',
  boxShadow: 'none',
  gap: '7px',
} as const;

export const btnPrimary = {
  ...btnBase,
  bgcolor: mq.primary,
  color: '#fff',
  border: `1px solid ${mq.primary}`,
  '&:hover': { bgcolor: mq.primaryHover, borderColor: mq.primaryHover, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: mq.borderStrong, borderColor: mq.borderStrong, color: '#fff' },
} as const;

export const btnSecondary = {
  ...btnBase,
  bgcolor: '#fff',
  color: mq.text,
  border: `1px solid ${mq.borderStrong}`,
  '&:hover': { bgcolor: mq.headBg, borderColor: mq.subtle, boxShadow: 'none' },
} as const;

export const btnDanger = {
  ...btnBase,
  bgcolor: '#fff',
  color: mq.red,
  border: `1px solid ${mq.redBorder}`,
  '&:hover': { bgcolor: mq.redBg, borderColor: mq.red, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#fff', borderColor: mq.cardBorder, color: mq.subtle },
} as const;

// Input matching `.field input/select` (1px border, 6px radius), enlarged to 14px.
export const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '6px',
    backgroundColor: '#fff',
    fontSize: '14px',
    '& fieldset': { borderColor: mq.cardBorder },
    '&:hover fieldset': { borderColor: mq.borderStrong },
    '&.Mui-focused fieldset': { borderColor: mq.primary, borderWidth: '1px' },
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(61,111,156,.12)' },
  },
  // The theme's default input colour is white (text.primary); force the dark body
  // colour on the typed value, the select display and the placeholder.
  '& .MuiInputBase-input': { color: mq.text, padding: '8px 10px' },
  '& .MuiInputBase-input::placeholder': { color: mq.subtle, opacity: 1 },
  '& .MuiSelect-select': { color: mq.text, padding: '8px 10px' },
  '& .MuiSelect-icon': { color: mq.muted },
  '& .MuiFormHelperText-root': { fontSize: '12px', lineHeight: 1.5, marginLeft: 0 },
  '& .MuiFormHelperText-root:not(.Mui-error)': { color: mq.subtle },
} as const;

// Dropdown options render in a portal, so field `sx` never reaches them — colour
// them explicitly or they stay white-on-white.
export const menuItemSx = { color: mq.text } as const;

// `.field label` — uppercase subtle, enlarged to 12px.
export const labelSx = {
  display: 'block',
  mb: '6px',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: 1,
  letterSpacing: '0.4px',
  textTransform: 'uppercase',
  // Essential labels: muted (#475569 ≈ 7:1 on white) meets WCAG AA, unlike subtle.
  color: mq.muted,
} as const;

export const reqSx = { color: mq.red, ml: '3px' } as const;

// Immutable fields (username, email in edit mode): greyed background + muted text
// so the locked state is obvious without relying solely on the helperText hint.
export const lockedFieldSx = {
  '& .MuiOutlinedInput-root': { backgroundColor: mq.headBg },
  '& .MuiInputBase-input': { color: mq.muted },
} as const;
